import React from "react";
import { Link } from "react-router-dom";
import { Crown } from "lucide-react";

/* Premium upgrade modal, shown when a non-admin / non-premium user taps a
   premium-gated feature (Academy / Upload & Learn / AI Tutor / Quiz Battle,
   and the Homepage AI Tutor Spotlight). Admin emails listed in adminCheck.js
   bypass this entirely. Copy is intentionally friendly + kid-safe and never
   says "buy" so we can swap to a real Stripe checkout later without re-wording. */
const PremiumUpgradeModal = ({ featureName, onClose, audience = "parent" }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
    onClick={onClose}
    data-testid="premium-upgrade-modal"
  >
    <div
      className="relative w-full max-w-md rounded-3xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/95 via-yellow-950/95 to-orange-950/95 p-6 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
      style={{ boxShadow: "0 0 60px rgba(251,191,36,0.25)" }}
    >
      <div className="flex flex-col items-center text-center">
        <div
          className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center mb-3 shadow-lg"
          style={{ boxShadow: "0 0 30px rgba(251,191,36,0.45)" }}
        >
          <Crown size={32} className="text-amber-950" />
        </div>
        <div className="text-amber-300 text-[10px] font-bold uppercase tracking-widest mb-1">Premium Feature</div>
        <h3 className="text-white font-fredoka font-extrabold text-2xl mb-2">{featureName}</h3>
        {audience === "child" ? (
          <>
            <p className="text-white/70 text-sm leading-relaxed mb-1">
              {featureName} is a <span className="text-amber-300 font-bold">Premium</span> feature. Ask a grown-up to unlock it for you!
            </p>
            <p className="text-white/45 text-xs mb-5">
              A parent can turn on premium for your explorer in Parent Corner, then you&apos;ll get lessons, AI tutors, homework help, and battles.
            </p>
            <button
              onClick={onClose}
              data-testid="premium-modal-close"
              className="w-full py-2.5 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 text-sm font-fredoka font-extrabold flex items-center justify-center gap-1.5 shadow-lg"
            >
              <Crown size={14} /> Ask a parent to unlock
            </button>
          </>
        ) : (
          <>
            <p className="text-white/70 text-sm leading-relaxed mb-1">
              Upgrade to <span className="text-amber-300 font-bold">Premium</span> to unlock {featureName} and every other golden-crown feature in Isaiah&apos;s World.
            </p>
            <p className="text-white/45 text-xs mb-5">
              Personalized lessons, AI tutors, homework scanning, and live battles, all included.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <button
                onClick={onClose}
                data-testid="premium-modal-close"
                className="flex-1 py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-white/80 text-sm font-bold border border-white/15"
              >
                Maybe later
              </button>
              <Link
                to="/pricing"
                onClick={onClose}
                data-testid="premium-modal-upgrade"
                className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-amber-950 text-sm font-fredoka font-extrabold flex items-center justify-center gap-1.5 shadow-lg"
              >
                <Crown size={14} /> Upgrade for access
              </Link>
            </div>
          </>
        )}
        <Link
          to="/learning-features"
          onClick={onClose}
          data-testid="premium-modal-see-inside"
          className="mt-3 text-amber-300/90 hover:text-amber-200 text-xs font-bold underline underline-offset-2"
        >
          See everything inside
        </Link>
      </div>
    </div>
  </div>
);

export default PremiumUpgradeModal;
